import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Upload, FileDown, Loader2, CheckCircle2, AlertTriangle,
  ShieldAlert, HelpCircle, PlayCircle, Plus, Repeat2, Ban, Building2,
} from 'lucide-react';
import { formazioneAlignApi, companiesApi } from '../lib/api';
import { toast } from 'sonner';

const MATCH_STYLE = {
  CERTO:       { label: 'Certo',       bg: 'bg-emerald-100', color: 'text-emerald-800', icon: CheckCircle2 },
  PROBABILE:   { label: 'Probabile',   bg: 'bg-amber-100',   color: 'text-amber-900',   icon: AlertTriangle },
  AMBIGUO:     { label: 'Ambiguo',     bg: 'bg-orange-100',  color: 'text-orange-900',  icon: ShieldAlert },
  NON_TROVATO: { label: 'Non trovato', bg: 'bg-slate-100',   color: 'text-slate-700',   icon: HelpCircle },
};

const ACTION_STYLE = {
  NEW:                  { label: 'NEW',       bg: 'bg-sky-100',    color: 'text-sky-800',    icon: Plus },
  RINNOVO:              { label: 'RINNOVO',   bg: 'bg-violet-100', color: 'text-violet-800', icon: Repeat2 },
  DA_AGGIORNARE_TITOLO: { label: 'TITOLO',    bg: 'bg-amber-100',  color: 'text-amber-900',  icon: Repeat2 },
  GIA_PRESENTE:         { label: 'GIÀ',       bg: 'bg-slate-100',  color: 'text-slate-700',  icon: CheckCircle2 },
  NON_APPLICABILE:      { label: 'N/A',       bg: 'bg-red-50',     color: 'text-red-700',    icon: Ban },
  ERRORE_DATI:          { label: 'ERR',       bg: 'bg-red-100',    color: 'text-red-800',    icon: Ban },
};

const Pill = ({ style, text }) => {
  const Ic = style.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${style.bg} ${style.color}`}>
      <Ic size={10} /> {text || style.label}
    </span>
  );
};

const FormazioneAlignImportPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [filterMatch, setFilterMatch] = useState('ALL');
  const [filterAction, setFilterAction] = useState('ALL');
  const [applying, setApplying] = useState(false);
  const [applyState, setApplyState] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [companyQuery, setCompanyQuery] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await companiesApi.list({ status: 'attive' });
        const arr = Array.isArray(data) ? data : (data?.items || []);
        arr.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'it'));
        setCompanies(arr);
      } catch (e) {
        // silenzioso: il selettore resta vuoto ma l'import senza scope funziona
      }
    })();
  }, []);

  const filteredCompanies = useMemo(() => {
    const q = companyQuery.trim().toLowerCase();
    if (!q) return companies.slice(0, 200);
    return companies.filter((c) => (c.name || '').toLowerCase().includes(q)).slice(0, 200);
  }, [companies, companyQuery]);

  const selectedCompanyName = useMemo(
    () => companies.find((c) => c.id === companyId)?.name || '',
    [companies, companyId],
  );

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setPreview(null);
    setSelectedRows(new Set());
    setApplyState(null);
  };

  const uploadPreview = async () => {
    if (!file) { toast.error('Seleziona un file Excel'); return; }
    setUploading(true);
    setPreview(null);
    setApplyState(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (companyId) fd.append('companyId', companyId);
      const { data: init } = await formazioneAlignApi.previewImport(fd);
      const token = init.token;
      // Polling loop
      let attempts = 0;
      const maxAttempts = 60; // 60 * 3s = 180s max
      const poll = async () => {
        const { data: st } = await formazioneAlignApi.previewStatus(token);
        setApplyState({ state: st.state, phase: st.phase, progress: st.progress, total: st.total });
        if (st.state === 'done') {
          setPreview({
            token,
            filename: st.filename,
            counters: st.counters,
            duplicati_excel: st.duplicati_excel,
            total_rows: st.total_rows,
            elapsed_sec: st.elapsed_sec,
            anomalies_total: st.anomalies_total,
            rows: (st.rows_first || []).concat(st.anomalies || []),
          });
          const c = st.counters;
          toast.success(
            `${st.total_rows} righe · NEW ${c.NEW} · RINN ${c.RINNOVO} · TITOLO ${c.DA_AGGIORNARE_TITOLO} · GIÀ ${c.GIA_PRESENTE} · AMB ${c.AMBIGUO} · N/T ${c.NON_TROVATO} · ⏱ ${st.elapsed_sec}s`
          );
          setUploading(false);
          setApplyState(null);
          return;
        }
        if (st.state === 'error') {
          toast.error(`Errore preview: ${st.error}`);
          setUploading(false);
          setApplyState(null);
          return;
        }
        attempts++;
        if (attempts < maxAttempts) setTimeout(poll, 3000);
        else { toast.error('Preview troppo lento, riprova'); setUploading(false); setApplyState(null); }
      };
      poll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore preview');
      setUploading(false);
    }
  };

  const filteredRows = useMemo(() => {
    if (!preview) return [];
    return preview.rows.filter((r) => {
      if (filterMatch !== 'ALL' && r.match_state !== filterMatch) return false;
      if (filterAction !== 'ALL' && r.action_state !== filterAction) return false;
      return true;
    });
  }, [preview, filterMatch, filterAction]);

  const toggleRow = (rn) => setSelectedRows((prev) => {
    const nx = new Set(prev);
    if (nx.has(rn)) nx.delete(rn); else nx.add(rn);
    return nx;
  });

  const toggleAllVisible = () => {
    if (!preview) return;
    const eligible = filteredRows.filter((r) =>
      (r.action_state === 'NEW' || r.action_state === 'UPDATE') && r.worker && (!r.errors || r.errors.length === 0),
    );
    const allSelected = eligible.every((r) => selectedRows.has(r.row_number));
    setSelectedRows((prev) => {
      const nx = new Set(prev);
      if (allSelected) eligible.forEach((r) => nx.delete(r.row_number));
      else eligible.forEach((r) => nx.add(r.row_number));
      return nx;
    });
  };

  const doApply = async () => {
    if (!preview) return;
    if (!window.confirm(
      `Applicare le modifiche al DB?\n\n` +
      `· NEW (nuovi inserimenti): ${preview.counters.NEW || 0}\n` +
      `· RINNOVO (insert affiancati): ${preview.counters.RINNOVO || 0}\n` +
      `· DA_AGGIORNARE_TITOLO (update solo titolo): ${preview.counters.DA_AGGIORNARE_TITOLO || 0}\n\n` +
      `SALTATE: ${preview.counters.AMBIGUO || 0} AMBIGUO + ${preview.counters.NON_TROVATO || 0} NON_TROVATO + ${preview.counters.NON_APPLICABILE || 0} N/A + ${preview.counters.GIA_PRESENTE || 0} già presenti.\n\n` +
      `Backup automatico via aggregation $out prima delle scritture.`,
    )) return;
    setApplying(true);
    // Applica tutte le righe applicabili in cache — il backend filtra per action_state
    const allRowNumbers = (preview.rows || []).map((r) => r.row_number);
    setApplyState({ state: 'starting', phase: 'starting', progress: 0, total: 0 });
    try {
      const { data } = await formazioneAlignApi.applyImport({
        token: preview.token,
        row_numbers: allRowNumbers,
      });
      const opId = data.opId;
      const poll = setInterval(async () => {
        try {
          const { data: st } = await formazioneAlignApi.applyStatus(opId);
          setApplyState({ ...st, opId });
          if (st.state === 'done') {
            clearInterval(poll);
            toast.success(`Inserite: ${st.total_inserted || 0} · Titoli aggiornati: ${st.total_titles_updated || 0} · Skip: ${st.skipped_present || 0}`);
            setApplying(false);
          } else if (st.state === 'error') {
            clearInterval(poll);
            toast.error(`Errore: ${st.error}`);
            setApplying(false);
          }
        } catch (err) {
          clearInterval(poll);
          setApplying(false);
        }
      }, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore apply');
      setApplying(false);
    }
  };

  const c = preview?.counters || {};
  const total = preview?.total_rows || 0;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-900" data-testid="fa-back">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[#023F80]">Allineamento formazione via Excel</h1>
            <p className="text-sm text-slate-500">
              Match CF → fallback Nome+Cognome · 4 stati match × 3 azioni (NEW / RINNOVO / PRESENT) · <strong>storia preservata: nessun delete</strong>.
            </p>
          </div>
        </div>
        <button
          onClick={() => formazioneAlignApi.downloadTemplate()}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-white border border-slate-200 hover:bg-slate-50"
          data-testid="fa-download-template"
        >
          <FileDown size={14} /> Scarica template
        </button>
      </div>

      {/* Uploader */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <p className="text-sm font-semibold text-slate-700 mb-1">1. Carica il file Excel</p>
        <p className="text-xs text-slate-500 mb-3">
          Colonne attese: <code>CodiceFiscale</code> (opz.), <code>Cognome</code>, <code>Nome</code>, <code>Categoria</code> o <code>Corso</code>,
          <code> DataRilascio</code> (obbl.), <code>Scadenza</code>, <code>Durata</code>. La <em>categoria</em> ha priorità; in mancanza si classifica il <em>corso</em>.
          <br />
          <span className="text-emerald-700">Formati alternativi supportati:</span> Matrix (Caprili), Long-per-sheet (Franci), Long-unified <em>Ruoli/Cognome/Nome/Scadenza</em> (Enoteca).
        </p>

        <div className="mb-3">
          <label className="block text-xs font-semibold text-slate-700 mb-1">
            <Building2 size={12} className="inline mr-1 -mt-0.5" />
            Azienda (opzionale) — se specificata, i match ambigui diventano certi e le
            righe senza CF vengono riconosciute anche con nome leggermente diverso
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={companyQuery}
              onChange={(e) => setCompanyQuery(e.target.value)}
              placeholder="Filtra aziende…"
              className="text-xs px-3 py-2 rounded-lg border border-slate-200 w-64 focus:outline-none focus:ring-2 focus:ring-[#023F80]/30"
              data-testid="fa-company-search"
            />
            <select
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              data-testid="fa-company-select"
              className="text-xs px-3 py-2 rounded-lg border border-slate-200 min-w-[280px] max-w-[420px] focus:outline-none focus:ring-2 focus:ring-[#023F80]/30"
            >
              <option value="">— Nessuna (match globale, com'era prima) —</option>
              {filteredCompanies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {companyId && (
              <button
                type="button"
                onClick={() => { setCompanyId(''); setCompanyQuery(''); }}
                className="text-xs text-slate-500 hover:text-slate-800 underline"
                data-testid="fa-company-clear"
              >
                Rimuovi selezione
              </button>
            )}
            {selectedCompanyName && (
              <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                Scope: {selectedCompanyName}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="file"
            accept=".xlsx,.xlsm,.xlsb"
            onChange={onFileChange}
            data-testid="fa-file-input"
            className="text-xs file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
          />
          <button
            onClick={uploadPreview}
            disabled={!file || uploading}
            data-testid="fa-preview-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#023F80] text-white disabled:opacity-60"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading
              ? (applyState?.phase
                  ? `${applyState.phase}${applyState.total ? ` ${applyState.progress || 0}/${applyState.total}` : '…'}`
                  : 'Analizzo…')
              : 'Anteprima'}
          </button>
        </div>
      </div>

      {preview && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-semibold text-slate-700">
              2. Anteprima · <span className="text-slate-500 font-normal">{preview.filename} · {total} righe · elab. {preview.elapsed_sec}s</span>
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => formazioneAlignApi.downloadReport(preview.token)}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg font-semibold bg-white border border-slate-200 hover:bg-slate-50"
                data-testid="fa-download-preview-report"
              >
                <FileDown size={12} /> Scarica report dettagliato (.xlsx)
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {[
              ['CERTO (CF)', c.CERTO, 'emerald'],
              ['PROBABILE (nome)', c.PROBABILE, 'amber'],
              ['AMBIGUO', c.AMBIGUO, 'orange'],
              ['NON TROVATO', c.NON_TROVATO, 'slate'],
              ['NEW', c.NEW, 'sky'],
              ['RINNOVO', c.RINNOVO, 'violet'],
              ['DA AGGIORNARE TITOLO', c.DA_AGGIORNARE_TITOLO, 'amber'],
              ['GIÀ PRESENTE', c.GIA_PRESENTE, 'slate'],
              ['NON APPLICABILE', c.NON_APPLICABILE, 'red'],
              ['ERRORE DATI', c.ERRORE_DATI, 'red'],
              ['Duplicati Excel', preview.duplicati_excel, 'slate'],
              ['Conflitti titolo', preview.title_conflicts_count, (preview.title_conflicts_count > 0 ? 'red' : 'emerald')],
            ].map(([label, val, tone]) => (
              <div key={label} className={`px-3 py-2 rounded-lg border bg-${tone}-50 border-${tone}-200`}>
                <div className={`text-[10px] font-semibold uppercase tracking-wider text-${tone}-800`}>{label}</div>
                <div className={`text-lg font-bold text-${tone}-900`}>{val || 0}</div>
              </div>
            ))}
          </div>

          {(preview.title_conflicts_count > 0) && (
            <div className="text-xs bg-red-50 border border-red-200 rounded-lg p-3 text-red-800">
              ⚠️ {preview.title_conflicts_count} record esistenti sarebbero aggiornati da più righe Excel. Il codice li tratta uno alla volta, ma verifica il report prima di applicare.
            </div>
          )}

          <div className="text-xs bg-blue-50 border border-blue-200 rounded-lg p-3 text-blue-900">
            ℹ️ <strong>Prima di applicare</strong>: verranno saltate <strong>{c.AMBIGUO || 0}</strong> righe AMBIGUO e <strong>{c.NON_TROVATO || 0}</strong> righe NON TROVATO.
            Backup automatico via aggregation <code>$out</code> di <code>formazione</code> prima delle scritture (rollback disponibile).
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100">
            <span className="text-xs text-slate-500">
              Righe applicabili (NEW + RINNOVO + DA_AGGIORNARE_TITOLO): <strong>{(c.NEW || 0) + (c.RINNOVO || 0) + (c.DA_AGGIORNARE_TITOLO || 0)}</strong>
            </span>
            <div className="flex-1" />
            <button
              onClick={doApply}
              disabled={applying}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
              data-testid="fa-apply-btn"
            >
              {applying ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
              {applying ? 'Applico…' : 'Applica formazione al DB'}
            </button>
          </div>

          {applyState && (
            <div className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-slate-700">Stato:</span>
                <span className="text-slate-600">{applyState.phase || applyState.state}</span>
                {applyState.state === 'done' && <CheckCircle2 size={14} className="text-emerald-600" />}
              </div>
              {applyState.total > 0 && (
                <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-2 bg-emerald-500 transition-all"
                    style={{ width: `${Math.round(((applyState.progress || 0) / applyState.total) * 100)}%` }} />
                </div>
              )}
              <p className="text-slate-500 mt-1">
                {applyState.progress || 0} / {applyState.total || 0}
                {applyState.state === 'done' && (
                  <> · inserite: <strong>{applyState.total_inserted || 0}</strong> · titoli aggiornati: <strong>{applyState.total_titles_updated || 0}</strong> · saltate (già presenti): {applyState.skipped_present || 0}</>
                )}
              </p>
              {applyState.state === 'done' && applyState.opId && (
                <button
                  onClick={() => formazioneAlignApi.downloadApplyReport(applyState.opId)}
                  className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#023F80] hover:underline"
                >
                  <FileDown size={11} /> Scarica report esito applicazione
                </button>
              )}
            </div>
          )}

          <div className="overflow-x-auto max-h-[62vh] overflow-y-auto border border-slate-100 rounded-xl">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-2 py-2 text-left">Riga</th>
                  <th className="px-2 py-2 text-left">Match</th>
                  <th className="px-2 py-2 text-left">Azione</th>
                  <th className="px-2 py-2 text-left">Input Excel</th>
                  <th className="px-2 py-2 text-left">Categoria/Corso</th>
                  <th className="px-2 py-2 text-left">Date</th>
                  <th className="px-2 py-2 text-left">Match worker</th>
                  <th className="px-2 py-2 text-left">Errori</th>
                </tr>
              </thead>
              <tbody>
                {(preview.rows || []).slice(0, 500).map((r) => (
                    <tr
                      key={r.row_number}
                      data-testid={`fa-row-${r.row_number}`}
                      className="border-b border-slate-50 hover:bg-slate-50/60"
                    >
                      <td className="px-2 py-2 font-mono text-[10px] text-slate-500">{r.row_number}</td>
                      <td className="px-2 py-2"><Pill style={MATCH_STYLE[r.match_state] || MATCH_STYLE.NON_TROVATO} /></td>
                      <td className="px-2 py-2"><Pill style={ACTION_STYLE[r.action_state] || ACTION_STYLE.NON_APPLICABILE} /></td>
                      <td className="px-2 py-2">
                        <div className="font-semibold">{r.input.lastName} {r.input.firstName}</div>
                        <div className="font-mono text-[10px] text-slate-400">{r.input.fiscalCode || 'CF assente'}</div>
                      </td>
                      <td className="px-2 py-2">
                        <div className="font-semibold">{r.input.corso}</div>
                        <div className="text-[10px] text-slate-500">{r.input.categoria}</div>
                      </td>
                      <td className="px-2 py-2">
                        <div>Ril.: <strong>{r.input.dataRilascio || '—'}</strong></div>
                        <div>Scad.: <span className="text-slate-500">{r.input.scadenza || '—'}</span></div>
                      </td>
                      <td className="px-2 py-2">
                        {r.worker ? (
                          <div>
                            <div className="font-semibold">{r.worker.lastName} {r.worker.firstName}</div>
                            <div className="text-[10px] text-slate-500">{r.worker.companyName}</div>
                          </div>
                        ) : r.candidates?.length > 0 ? (
                          <div>
                            <div className="text-orange-700 font-semibold">{r.candidates.length} candidati:</div>
                            {r.candidates.slice(0, 2).map((cd) => (
                              <div key={cd.id} className="text-[10px] text-slate-500">· {cd.lastName} {cd.firstName} — {cd.companyName}</div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-400">{r.match_reason}</span>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        {(r.errors || []).length > 0 && (
                          <div className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
                            {r.errors.join('; ')}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <div className="text-[10px] text-slate-400 text-center py-2">Mostrate prime 500 righe · scarica il report per l'elenco completo</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormazioneAlignImportPage;
